/* French collection title order across the complete filtered collection. */
(function () {
  'use strict';

  var CACHE_MS = 60 * 1000;
  var MAX_INDEX_PAGES = 100;
  var indexCache = new Map();
  var cardCache = new Map();
  var collator = new Intl.Collator('fr-CA', { sensitivity: 'base', numeric: true });
  var suffix = /\s*\[(?:backorder|en\s+r[ée]approvisionnement|pre[\s-]?order|pr[ée]commande)[^\]]*\]\s*$/i;

  function cleanTitle(title) {
    var value = String(title || '').trim();
    while (suffix.test(value)) value = value.replace(suffix, '').trim();
    return value;
  }

  function cached(map, key) {
    var entry = map.get(key);
    if (!entry) return null;
    if (entry.expires <= Date.now()) {
      map.delete(key);
      return null;
    }
    return entry.value;
  }

  function remember(map, key, value) {
    map.set(key, { value: value, expires: Date.now() + CACHE_MS });
    return value;
  }

  function collectionUrl(page) {
    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.set('view', 'sv-sort-index');
    url.searchParams.set('sort_by', 'manual');
    url.searchParams.set('page', String(page));
    return url;
  }

  async function fetchText(url, signal) {
    var response = await fetch(url.href || url, {
      credentials: 'same-origin', signal: signal, headers: { 'Accept': 'text/html, application/json' }
    });
    if (!response.ok) throw new Error('HTTP ' + response.status);
    return response.text();
  }

  async function indexPage(url, signal) {
    var body = await fetchText(url, signal);
    var data = JSON.parse(body);
    if (!data || !Number.isInteger(data.page) || !Number.isInteger(data.pages) ||
        !Number.isInteger(data.total) || !Array.isArray(data.products) ||
        data.page < 1 || data.pages < 1 || data.pages > MAX_INDEX_PAGES ||
        data.total < 0 || data.page > data.pages || data.page > MAX_INDEX_PAGES ||
        data.products.length > 250 || !/^fr(?:-|$)/i.test(String(data.locale || ''))) {
      throw new Error('Invalid localized collection index');
    }
    return data;
  }

  async function mapBounded(items, limit, fn) {
    var result = new Array(items.length);
    var cursor = 0;
    var workers = [];
    for (var w = 0; w < Math.min(limit, items.length); w++) {
      workers.push((async function () {
        while (cursor < items.length) {
          var i = cursor++;
          result[i] = await fn(items[i], i);
        }
      })());
    }
    await Promise.all(workers);
    return result;
  }

  async function completeIndex(signal) {
    var firstUrl = collectionUrl(1);
    var key = firstUrl.href.replace(/([?&])page=1(?=&|$)/, '$1page=*');
    var stored = cached(indexCache, key);
    if (stored) return stored;

    var first = await indexPage(firstUrl, signal);
    if (first.page !== 1) throw new Error('Index page mismatch');
    var pageNumbers = [];
    for (var n = 2; n <= first.pages; n++) pageNumbers.push(n);
    var rest = await mapBounded(pageNumbers, 2, async function (page) {
      var data = await indexPage(collectionUrl(page), signal);
      if (data.page !== page || data.pages !== first.pages || data.total !== first.total ||
          data.locale !== first.locale) throw new Error('Index pagination mismatch');
      return data;
    });

    var byId = new Map();
    [first].concat(rest).forEach(function (batch) {
      batch.products.forEach(function (product) {
        if (!product || !/^\d+$/.test(String(product.id)) ||
            typeof product.title !== 'string' || !product.title.trim() ||
            typeof product.url !== 'string' || !product.url.trim()) {
          throw new Error('Invalid indexed product');
        }
        var id = String(product.id);
        if (byId.has(id)) throw new Error('Duplicate indexed product');
        byId.set(id, { id: id, title: product.title, url: product.url });
      });
    });
    if (byId.size !== first.total) throw new Error('Incomplete localized collection index');
    return remember(indexCache, key, Array.from(byId.values()));
  }

  function compareIds(a, b) {
    // IDs are decimal strings and may exceed JavaScript's exact integer range.
    return a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);
  }

  function sortedProducts(products, direction) {
    var multiplier = direction === 'title-descending' ? -1 : 1;
    return products.slice().sort(function (a, b) {
      return multiplier * collator.compare(cleanTitle(a.title), cleanTitle(b.title)) || compareIds(a.id, b.id);
    });
  }

  function cardUrl(productUrl) {
    var url = new URL(productUrl, window.location.origin);
    if (url.origin !== window.location.origin || !/\/products\//.test(url.pathname)) {
      throw new Error('Invalid product card URL');
    }
    url.searchParams.set('view', 'sv-sort-card');
    var pageParams = new URL(window.location.href).searchParams;
    var preview = pageParams.get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
    var currency = pageParams.get('currency');
    if (currency && /^[A-Z]{3}$/i.test(currency)) url.searchParams.set('currency', currency.toUpperCase());
    return url;
  }

  async function fetchCard(product, signal) {
    var url = cardUrl(product.url);
    var key = url.href;
    var stored = cached(cardCache, key);
    if (stored) return stored;
    var html = await fetchText(url, signal);
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var cards = doc.querySelectorAll('.sv-sort-card');
    if (cards.length !== 1 || !cards[0].innerHTML.trim()) throw new Error('Invalid product card');
    var cardId = cards[0].getAttribute('data-sv-product-id');
    if (cardId && cardId !== product.id) throw new Error('Product card ID mismatch');
    return remember(cardCache, key, cards[0].innerHTML);
  }

  function refreshThemeHooks() {
    var main = window.AT_Main;
    if (!main) return;
    ['filterCatalog', 'fixButton', 'fixTitle', 'handleReviews',
      'switchImgProduct', 'handleGridList', 'init_CountDown'].forEach(function (name) {
      if (typeof main[name] === 'function') {
        try { main[name](); } catch (error) { console.error('Collection card hook failed:', name, error); }
      }
    });
  }

  class LocalizedCollectionSort extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected) return;
      if (this._controller) return;
      this._grid = this.parentElement && this.parentElement.querySelector('.cata-product.cp-grid');
      if (!this._grid) return;
      this._originalCards = new Map();
      this._grid.querySelectorAll('[data-sv-product-id]').forEach((node) => {
        var id = node.getAttribute('data-sv-product-id');
        if (id && !this._originalCards.has(id)) this._originalCards.set(id, node.innerHTML);
      });
      var current = this._grid.querySelector('.mode-view-item');
      var match = current && current.className.match(/\bnumber-\d+\b/);
      this._numberClass = match ? match[0] : 'number-4';
      this._controller = new AbortController();
      this._runId = 0;
      this.run();
    }

    disconnectedCallback() {
      if (this._controller) this._controller.abort();
      this._controller = null;
      this._runId++;
    }

    isCurrent(runId) {
      return this.isConnected && this._controller && !this._controller.signal.aborted && this._runId === runId;
    }

    status(message, retry) {
      this.replaceChildren();
      var status = document.createElement('p');
      status.setAttribute('role', retry ? 'alert' : 'status');
      status.setAttribute('aria-live', retry ? 'assertive' : 'polite');
      status.textContent = message;
      this.appendChild(status);
      if (retry) {
        var button = document.createElement('button');
        button.type = 'button';
        button.textContent = this.dataset.retry || 'Réessayer';
        button.addEventListener('click', () => this.run(), { once: true });
        this.appendChild(button);
      }
    }

    async run() {
      if (!this._grid || !this._controller) return;
      if (this._runId) this._controller.abort();
      this._controller = new AbortController();
      var runId = ++this._runId;
      var signal = this._controller.signal;
      this.setAttribute('aria-busy', 'true');
      this.dataset.state = 'loading';
      delete this.dataset.loadedTotal;
      this._grid.hidden = true;
      this.status(this.dataset.loading || 'Chargement…', false);

      try {
        var direction = this.dataset.sort;
        var page = Number(this.dataset.page);
        var pageSize = Number(this.dataset.pageSize);
        var total = Number(this.dataset.total);
        if (!/^(title-ascending|title-descending)$/.test(direction) ||
            !Number.isInteger(page) || page < 1 ||
            !Number.isInteger(pageSize) || pageSize < 1 || pageSize > 250 ||
            !Number.isInteger(total) || total < 0) throw new Error('Invalid collection sort configuration');

        var products = await completeIndex(signal);
        if (!this.isCurrent(runId)) return;
        if (products.length !== total) throw new Error('Collection count changed');
        this.dataset.loadedTotal = String(products.length);
        if (page > Math.max(1, Math.ceil(total / pageSize))) throw new Error('Collection page out of range');
        var sorted = sortedProducts(products, direction);
        var selected = sorted.slice((page - 1) * pageSize, page * pageSize);
        if (selected.length !== Math.max(0, Math.min(pageSize, total - (page - 1) * pageSize))) {
          throw new Error('Collection page mismatch');
        }
        var html = await mapBounded(selected, 4, (product) => {
          if (this._originalCards.has(product.id)) return this._originalCards.get(product.id);
          return fetchCard(product, signal);
        });
        if (!this.isCurrent(runId)) return;

        var fragment = document.createDocumentFragment();
        selected.forEach((product, i) => {
          var wrapper = document.createElement('div');
          wrapper.className = 'mode-view-item products-grid-item ' + this._numberClass;
          wrapper.dataset.svProductId = product.id;
          wrapper.innerHTML = html[i];
          fragment.appendChild(wrapper);
        });
        this._grid.replaceChildren(fragment);
        this._grid.hidden = false;
        this.removeAttribute('aria-busy');
        this.dataset.state = 'ready';
        this.replaceChildren();
        refreshThemeHooks();
        this.dispatchEvent(new CustomEvent('sv:localized-collection-sort-ready', { bubbles: true }));
      } catch (error) {
        if (!this.isCurrent(runId)) return;
        console.error('Localized collection sort failed:', error);
        this._grid.hidden = true;
        this.removeAttribute('aria-busy');
        this.dataset.state = 'error';
        this.status(this.dataset.error || 'Le tri est indisponible pour le moment.', true);
      }
    }
  }

  if (!customElements.get('sv-localized-collection-sort')) {
    customElements.define('sv-localized-collection-sort', LocalizedCollectionSort);
  }
})();
